import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiShoppingCart, 
  FiArrowLeft, 
  FiPlay,
  FiClock,
  FiUser,
  FiCheck,
  FiCreditCard,
  FiShield,
  FiLock
} from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import dashboardEvents from '../utils/dashboardEvents';
import api from '../utils/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { items, total, clearCart } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const handleBackToCart = () => {
    navigate('/cart');
  };

  const handlePayment = async () => {
    if (items.length === 0) {
      toast.error('장바구니가 비어있습니다');
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('로그인이 필요합니다');
      navigate('/login');
      return;
    }

    // Validate that all items have required properties
    const invalidItems = items.filter(item => !item._id || !item.title);
    if (invalidItems.length > 0) {
      toast.error('장바구니에 유효하지 않은 강의가 있습니다');
      return;
    }

    setLoading(true);
    try {
      // Always bypass real payments: use sandbox checkout to enroll immediately
      const payload = {
        items: items.map(c => ({ courseId: c._id, amount: c.price || 0 }))
      };
      const resp = await api.post('/api/payments/sandbox/checkout', payload);
      if (!resp.data?.success) {
        throw new Error('Sandbox checkout failed');
      }

      // Clear cart after successful payment
      clearCart();

      // Emit events for dashboard updates
      const purchasedCourses = items.map(course => ({
        courseId: course._id,
        courseTitle: course.title || 'Unknown Course',
        amount: course.price || 0,
        purchasedAt: new Date()
      }));

      dashboardEvents.emit('course-purchased', {
        courses: purchasedCourses,
        totalAmount: total,
        userId: user._id
      });

      dashboardEvents.emit('enrollment-added', {
        courses: purchasedCourses,
        userId: user._id
      });

      dashboardEvents.emit('stats-updated', {
        type: 'purchase',
        data: {
          courses: purchasedCourses,
          totalAmount: total
        }
      });

      // Refresh user data to get updated enrolled courses
      await refreshUser();

      // Force a small delay to ensure user data is updated
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.success('결제가 완료되었습니다! (샌드박스)');
      navigate('/payment/success');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('결제 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button
              onClick={() => navigate('/cart')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <FiArrowLeft className="w-5 h-5 mr-2" />
              장바구니로 돌아가기
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">결제</h1>
            <p className="text-gray-600">구매를 완료하고 학습을 시작하세요</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FiShoppingCart className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">장바구니가 비어있습니다</h2>
            <p className="text-gray-600 mb-8">결제할 강의가 없습니다</p>
            <button
              onClick={() => navigate('/courses')}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              강의 둘러보기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBackToCart}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="w-5 h-5 mr-2" />
            장바구니로 돌아가기
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">결제</h1>
          <p className="text-gray-600">구매를 완료하고 학습을 시작하세요</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">주문 요약</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {items.map((course) => (
                  <div key={course._id} className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Course Thumbnail */}
                      <div className="flex-shrink-0">
                        <img
                          src={course.thumbnail || '/images/default-course.svg'}
                          alt={course.title}
                          className="w-20 h-16 object-cover rounded-lg"
                        />
                      </div>

                      {/* Course Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {course.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-2">
                          {course.subtitle}
                        </p>
                        
                        {/* Course Details */}
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                          <div className="flex items-center">
                            <FiUser className="w-4 h-4 mr-1" />
                            <span>{course.createdBy?.name || course.instructor?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center">
                            <FiClock className="w-4 h-4 mr-1" />
                            <span>{course.totalDuration || 0}시간</span>
                          </div>
                          <div className="flex items-center">
                            <FiPlay className="w-4 h-4 mr-1" />
                            <span>{course.totalLessons || 0}강의</span>
                          </div>
                        </div>

                        {/* Course Features */}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <div className="flex items-center">
                            <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                            <span>평생 수강</span>
                          </div>
                          <div className="flex items-center">
                            <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                            <span>수료증</span>
                          </div>
                          <div className="flex items-center">
                            <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                            <span>모바일 지원</span>
                          </div>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          ₩{(course.price || 0).toLocaleString()}
                        </div>
                        {course.originalPrice && course.price && course.originalPrice > course.price && (
                          <div className="text-sm text-gray-500 line-through">
                            ₩{course.originalPrice.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow-sm mt-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">결제 방법</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="card"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <label htmlFor="card" className="flex items-center text-gray-900">
                      <FiCreditCard className="w-5 h-5 mr-2" />
                      신용카드
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="bank"
                      name="paymentMethod"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <label htmlFor="bank" className="flex items-center text-gray-900">
                      <FiShield className="w-5 h-5 mr-2" />
                      계좌이체
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">결제 요약</h2>
              
              {/* Summary Details */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">강의 수</span>
                  <span className="font-medium">{items.length}개</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">소계</span>
                  <span className="font-medium">₩{(total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">할인</span>
                  <span className="font-medium text-green-600">₩0</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">총 금액</span>
                    <span className="text-lg font-bold text-gray-900">₩{(total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment Button */}
              <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    결제 처리 중...
                  </>
                ) : (
                  <>
                    <FiLock className="w-5 h-5 mr-2" />
                    안전하게 결제하기
                  </>
                )}
              </button>

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">
                  <FiShield className="w-4 h-4 mr-2 text-green-500" />
                  <span>SSL 암호화 결제</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <FiCheck className="w-4 h-4 mr-2 text-green-500" />
                  <span>30일 환불 보장</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <FiCheck className="w-4 h-4 mr-2 text-green-500" />
                  <span>평생 수강 가능</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 